<template>
  <basic-modal
    ref="modal"
    :is-showing="isShowing"
    :title="title"
    :on-cancel="closeModal"
    :show-previous="currentStep > 1"
    :on-previous="() => currentStep -= 1"
    :on-submit="lastStep ? save : () => currentStep += 1"
    :submit-text="lastStep ? 'Opslaan' : 'Volgende'"
    :submit-icon="lastStep ? 'mdi-content-save' : 'mdi-chevron-right'"
    :submit-is-disabled="lastStep && !formCompleted"
    :is-pending="isPending"
    :error-message="errorMessage"
  >
    <resource-form
      ref="resourceForm"
      v-if="!multipleSteps && isShowing"
      :model.sync="model"
      :fields="fields"
      :is-pending="isPending"
      :disable-validation="!hasBeenTriedToSubmit"
      :is-completed.sync="isCompleted"
      new-resource
    />
    <v-stepper
      v-if="multipleSteps"
      v-model="currentStep"
      class="elevation-0"
      alt-labels
    >
      <v-stepper-header class="elevation-0">
        <template v-for="(step, id) in fields">
          <v-stepper-step
            :key="`step-${id}`"
            :step="id + 1"
            :complete="currentStep > id + 1"
            :rules="[() => completedSteps[id] !== false]"
          >
            {{ toHuman(step.title) }}
          </v-stepper-step>
          <v-divider
            v-if="id + 1 < fields.length"
            :key="`divider-${id}`"
          />
        </template>
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content
          v-for="(step, id) in fields"
          :key="id"
          :step="id + 1"
        >
          <resource-form
            v-if="isShowing"
            :ref="`resourceForm-${id}`"
            :model.sync="model[step.title]"
            :fields="step.fields"
            :is-pending="isPending"
            :disable-validation="!hasBeenTriedToSubmit"
            :is-completed="completedSteps[id]"
            new-resource
            @update:is-completed="isCompleted => completedSteps.splice(id, 1, isCompleted)"
          />
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </basic-modal>
</template>

<script>
import { VStepper, VStepperHeader, VStepperStep, VStepperItems, VStepperContent, VDivider } from 'vuetify/lib'
import { toHuman } from '@bit/streamline.web.utils'
import BasicModal from '@bit/streamline.web.basic-modal'
import ResourceForm from '@bit/streamline.web.resource-form'

export default {
  components: {
    VStepper,
    VStepperHeader,
    VStepperStep,
    VStepperItems,
    VStepperContent,
    VDivider,
    BasicModal,
    ResourceForm
  },
  props: {
    isShowing: {
      type: Boolean,
      required: true
    },
    title: {
      type: String,
      required: false,
      default: ''
    },
    fields: {
      type: Array,
      required: true
    },
    create: {
      type: Function,
      required: true
    },
    multipleSteps: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      currentStep: 1,
      model: {},
      isPending: false,
      isCompleted: true,
      errorMessage: '',
      completedSteps: this.fields.map(() => undefined),
      hasBeenTriedToSubmit: false
    }
  },
  computed: {
    formCompleted () {
      if (!this.multipleSteps) { return this.isCompleted }
      return this.completedSteps.every(stepCompleted => stepCompleted !== false)
    },
    // Create the default model with all defaults already set
    defaultModel () {
      if (this.multipleSteps) {
        // Loop over each step of the steps and get the defaults for that step
        return this.fields.reduce((model, step) => ({ ...model, [step.title]: this.getDefaultsForStep(step.fields) }), {})
      } else {
        // There is just a single step, so just get the defaults for this step
        return this.getDefaultsForStep(this.fields)
      }
    },
    lastStep () {
      if (!this.multipleSteps) { return true }
      return this.currentStep === this.fields.length
    }
  },
  watch: {
    fields () {
      if (this.multipleSteps) {
        const newModel = {}
        Object.keys(this.defaultModel).forEach(key => {
          newModel[key] = { ...this.model[key], ...this.defaultModel[key] }
        })
        this.model = newModel
      } else {
        this.model = { ...this.model, ...this.defaultModel }
      }
    }
  },
  mounted () {
    this.model = this.defaultModel
  },
  methods: {
    toHuman,
    getDefaultsForStep (fields) {
      const model = {}
      fields.forEach(row => {
        if (Array.isArray(row)) {
          // If the row is an array it means that it has multiple fields on the same row
          row.forEach(field => {
            if (field.default) { model[field.fieldName] = field.default }
          })
        } else if (row.default) {
          // Otherwise the row is just a field
          model[row.fieldName] = row.default
        }
      })
      return model
    },
    async save () {
      try {
        this.hasBeenTriedToSubmit = true
        if (this.multipleSteps) {
          let allStepsCompleted = true
          this.fields.forEach((step, id) => {
            if (!this.$refs[`resourceForm-${id}`][0].validateForm()) { allStepsCompleted = false }
          })
          if (!allStepsCompleted) { throw new Error('notComplete') }
        } else if (!this.$refs.resourceForm.validateForm()) { throw new Error('notComplete') }

        this.isPending = true
        await this.create(JSON.parse(JSON.stringify(this.model)))
        this.closeModal()
      } catch (error) {
        if (error.message !== 'notComplete') {
          this.$refs.modal.shake()
          this.errorMessage = error.message
          setTimeout(() => { this.errorMessage = '' }, 8000)
        }
      } finally {
        this.isPending = false
      }
    },
    closeModal () {
      this.currentStep = 1
      this.isCompleted = true
      this.$emit('update:isShowing', false)
    }
  }
}
</script>

<style scoped lang="scss">
  .v-stepper {
    .v-stepper__content {
      padding-top: 4px;
      padding-bottom: 0px;
    }
  }
</style>
